// src/app/services/documents.ts
import { DocumentsFiltersType } from 'app/features/documents/DocumentsFilters';
import { NodeForm } from 'app/features/documents/UpdateNodeForm';
import api, {
  buildQueryParams,
  IGetQueryParams,
  IPaginatedDirectoryRepsonse,
} from './index';
import { IClientBusiness, IProject } from './responseTypes';
import { ITask } from './tasks';
import { IUser } from './user';
export interface IDocument extends IDirectoryNode {}
export interface IDirectoryNode {
  id: number;
  created_at: string;
  parent_id?: number | null;
  title: string;
  creator: IUser;
  // client?: IUser;
  clients?: IUser[];
  client_id?: number; // available in notifcation
  business_id?: number; // available in notifcation
  business?: IClientBusiness;
  task?: ITask;
  projects?: IProject[];
  is_trashed: boolean;
  object_type: 'document' | 'folder';
  doctype: string; // Document
  description: string; // Document
  file_content_type: string; // Document
  file_url: string; // Document
  is_public?: boolean; // Folder
  documents_count?: number; // Folder
  subfolders_count?: number; // Folder
  path: string;
  task_id?: number;
  project_id?: number;
  project_ids?: number[];
  viewed_file?:
    | {
        user: { given_name: string; family_name: string };
        viewed_at: string;
      }[]
    | {
        user: { given_name: string; family_name: string };
        viewed_at: string;
      };
}

export const documentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    directoryNodeForm: builder.mutation<IDirectoryNode, Partial<NodeForm>>({
      query: (file_object) => ({
        url: `/file_objects/${file_object.id || ''}`,
        method: file_object.id ? 'PUT' : 'POST',
        body: file_object.file
          ? prepareDocumentFormData(file_object)
          : { file_object },
      }),
      invalidatesTags: [{ type: 'Documents', id: 'LIST' }, { type: 'Folders' }],
      transformResponse: (response: IPaginatedDirectoryRepsonse) =>
        response.file_object!,
    }),
    copyFileObject: builder.mutation({
      query: ({
        id,
        destination_parent_id,
      }: {
        id: number;
        destination_parent_id: number | null;
      }) => ({
        url: `/file_objects/${id}/copy_structure`,
        method: 'POST',
        body: { destination_parent_id },
      }),
    }),
    getDirectoryItems: builder.query<
      IPaginatedDirectoryRepsonse,
      IGetQueryParams<DocumentsFiltersType>
    >({
      query: (params) => `/file_objects/show${buildQueryParams(params, true)}`,
      transformResponse: (response: IPaginatedDirectoryRepsonse, args) => {
        return {
          file_object: response.file_object,
          data: response.data || [],
          pagination: response.pagination || {
            count: 0,
            page: 1,
            pages: 0,
            per_page: 10,
          },
        };
      },
    }),
    updateNode: builder.mutation({
      query: (file_object: Partial<IDirectoryNode | NodeForm>) => ({
        url: `/file_objects/${file_object.id}`,
        method: 'PUT',
        body: { file_object },
      }),
    }),
    deleteNode: builder.mutation({
      query: (id: number) => ({
        url: `/file_objects/${id}`,
        method: 'DELETE',
      }),
    }),
    moveToTrash: builder.mutation({
      query: ({
        id,
        destination_parent_id,
      }: {
        id: number;
        destination_parent_id: number | null;
      }) => ({
        url: `/file_objects/${id}/move_to_trash`,
        method: 'PATCH',
        body: { destination_parent_id },
      }),
    }),
    restore: builder.mutation({
      query: ({
        id,
        destination_parent_id,
      }: {
        id: number;
        destination_parent_id: number | null;
      }) => ({
        url: `/file_objects/${id}/restore`,
        method: 'PATCH',
        body: { destination_parent_id },
      }),
    }),
    getDocumentPermittedUsers: builder.query({
      query: ({
        resourceType,
        resourceId,
        permissionType,
      }: {
        resourceType: string;
        resourceId: number;
        permissionType: string;
      }) =>
        `/permissions/permitted_users?resource_type=${resourceType}&resource_id=${resourceId}&permission_type=${permissionType}`,
      providesTags: () => [{ type: 'documentPermissions' }],
    }),
    addDocumentPermissionToUser: builder.mutation({
      query: (permission: {
        permission_type: string;
        resource_type: string;
        resource_id: number;
        user_id: number;
      }) => ({
        url: `/permissions`,
        method: 'POST',
        body: { permission },
      }),
      invalidatesTags: [{ type: 'documentPermissions' }],
    }),
    deleteDocumentPermissionFromUser: builder.mutation({
      query: (permission: {
        permission_type: string;
        resource_type: string;
        resource_id: number;
        user_id: number;
      }) => ({
        url: `/permissions`,
        method: 'DELETE',
        body: { permission },
      }),
      invalidatesTags: [{ type: 'documentPermissions' }],
    }),
  }),
});
export const {
  useUpdateNodeMutation,
  useDeleteNodeMutation,
  useGetDocumentPermittedUsersQuery,
  useAddDocumentPermissionToUserMutation,
  useDeleteDocumentPermissionFromUserMutation,
  useLazyGetDocumentPermittedUsersQuery,
  useLazyGetDirectoryItemsQuery,
  useDirectoryNodeFormMutation,
  useCopyFileObjectMutation,
  useMoveToTrashMutation,
  useRestoreMutation,
} = documentsApi;
function prepareDocumentFormData(form: Partial<NodeForm>) {
  const formData = new FormData();
  const data = [
    ['title', form.title],
    ['name', form.title],
    ['doctype', form.doctype],
    ['description', form.description],
    ['parent_id', form.parent_id],
    ['client_id', form.client_id],
    ['business_id', form.business_id],
    ['project_id', form?.project_id],
    ['task_id', form.task_id],
    ['object_type', 'document'],
  ].filter(([name, value]) => !!value) as [string, string | File][];
  data.forEach(([name, value]) =>
    formData.append(`file_object[${name}]`, value),
  );
  form.file && formData.append(`file_object[file]`, form.file, form.title);
  return formData;
}
